import React from 'react';
import Button from '../Button';
import { useTeam } from '../../context/TeamContext';

interface WizardCompletionProps {
  teamId: number;
  teamName: string;
  onNavigate: (path: string) => void;
}

const WizardCompletion: React.FC<WizardCompletionProps> = ({
  teamId,
  teamName,
  onNavigate,
}) => {
  const { refreshTeams, setSelectedTeamId } = useTeam();

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-green-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center">
          {/* Success Icon */}
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/30 mb-4">
            <svg
              className="h-10 w-10 text-green-600 dark:text-green-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>

          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Team '{teamName}' created successfully!
          </h1>

          <div className="mt-8 p-6 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-md text-left">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">What's Next?</h2>
            <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>Team created and configured</span>
              </li>
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>Lineup format configured</span>
              </li>
            </ul>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="primary"
              size="lg"
              onClick={async () => {
                // Refresh teams to ensure the newly created team is in the list
                await refreshTeams();
                // Set the selected team ID so the modal doesn't show (persists to localStorage)
                setSelectedTeamId(teamId);
                // Navigate to the dashboard
                onNavigate('/teams/members');
              }}
            >
              Go to Team Dashboard
            </Button>
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button
              onClick={async () => {
                await refreshTeams();
                setSelectedTeamId(teamId);
                onNavigate('/teams/schedule');
              }}
              className="p-4 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 text-left transition-colors"
            >
              <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-1">Add More Matches</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Schedule upcoming matches</p>
            </button>

            <button
              onClick={async () => {
                await refreshTeams();
                setSelectedTeamId(teamId);
                onNavigate('/teams/members');
              }}
              className="p-4 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 text-left transition-colors"
            >
              <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-1">Manage Team Members</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Add or remove players</p>
            </button>

            <button
              onClick={async () => {
                await refreshTeams();
                setSelectedTeamId(teamId);
                onNavigate('/teams/availability');
              }}
              className="p-4 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 text-left transition-colors"
            >
              <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-1">Set Availability</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Track player availability</p>
            </button>

            <button
              onClick={async () => {
                await refreshTeams();
                setSelectedTeamId(teamId);
                onNavigate('/teams/strategize');
              }}
              className="p-4 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 text-left transition-colors"
            >
              <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-1">Create First Lineup</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Build your first match lineup</p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WizardCompletion;

